import { useEffect, useMemo, useState } from "react";
import { api, buildWaLink, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, MapPin, Users, MessageCircle, UserRound, Map as MapIcon } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const STATUS_COLOR = { Confirmed: "bg-emerald-500", "Payment Verification Pending": "bg-amber-500", Cancelled: "bg-muted-foreground" };
const ALL = "all";
const VIEWS = ["month", "week", "day"];

function range(view, cursor) {
  if (view === "month") { const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1); return [s, new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)]; }
  if (view === "week") { const s = addDays(cursor, -((cursor.getDay() + 6) % 7)); return [s, addDays(s, 7)]; }
  return [cursor, addDays(cursor, 1)];
}

export default function AdminCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [data, setData] = useState({ days: [] });
  const [loc, setLoc] = useState(ALL);
  const [fTrip, setFTrip] = useState(ALL);
  const [fEmp, setFEmp] = useState(ALL);
  const [fStatus, setFStatus] = useState(ALL);
  const [trips, setTrips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sel, setSel] = useState(null);

  const [start, end] = range(view, cursor);
  useEffect(() => { api.get("/admin/trips").then(({ data }) => setTrips(data)); if (isAdmin) api.get("/admin/employees").then(({ data }) => setEmployees(data)); }, [isAdmin]);
  useEffect(() => {
    const p = new URLSearchParams({ start: iso(start), end: iso(end) });
    if (fTrip !== ALL) p.set("trip_id", fTrip);
    if (fEmp !== ALL) p.set("employee_id", fEmp);
    if (fStatus !== ALL) p.set("status", fStatus);
    api.get(`/admin/calendar?${p}`).then(({ data }) => setData(data));
  }, [view, cursor, fTrip, fEmp, fStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const locations = useMemo(() => [...new Set(data.days.flatMap((d) => d.locations.map((l) => l.destination)))].sort(), [data]);
  const byDate = useMemo(() => {
    const m = {};
    data.days.forEach((d) => {
      const locs = loc === ALL ? d.locations : d.locations.filter((l) => l.destination === loc);
      if (locs.length) m[d.date] = { ...d, locations: locs, bookings: locs.reduce((a, l) => a + l.bookings, 0), travellers: locs.reduce((a, l) => a + l.travellers, 0) };
    });
    return m;
  }, [data, loc]);

  const totals = Object.values(byDate).reduce((a, d) => ({ b: a.b + d.bookings, t: a.t + d.travellers }), { b: 0, t: 0 });
  const todayStr = iso(new Date());
  const step = (n) => setCursor(view === "month" ? new Date(cursor.getFullYear(), cursor.getMonth() + n, 1) : addDays(cursor, n * (view === "week" ? 7 : 1)));
  const title = view === "month" ? cursor.toLocaleString("en-IN", { month: "long", year: "numeric" })
    : view === "week" ? `${start.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${addDays(end, -1).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
      : cursor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const monthCells = () => {
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const offset = (start.getDay() + 6) % 7;
    return [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => iso(new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)))];
  };
  const weekCells = () => Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));

  const DayCell = ({ ds, tall }) => {
    const info = byDate[ds];
    const day = Number(ds.slice(8));
    return (
      <button type="button" onClick={() => info && setSel(info)} data-testid={info ? "calendar-day-busy" : "calendar-day"}
        className={`relative border-b border-r p-1.5 text-left flex flex-col gap-1 transition-colors ${tall ? "min-h-[220px]" : ""} ${info ? "hover:bg-primary/5 cursor-pointer" : "cursor-default"} ${ds === todayStr ? "bg-primary/5" : ""}`}>
        <span className={`text-xs font-mono ${ds === todayStr ? "text-primary font-semibold" : "text-muted-foreground"}`}>{tall ? new Date(ds).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }) : day}</span>
        {info && (
          <>
            <span className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-1.5 py-0.5 text-[11px] font-medium w-fit" data-testid="day-summary"><Users className="h-3 w-3" /> {info.travellers} · {info.bookings} bkg</span>
            <div className="space-y-0.5 overflow-hidden">
              {info.locations.slice(0, tall ? 8 : 3).map((l) => (
                <p key={l.destination} className="text-[11px] truncate"><span className="font-medium">{l.destination}</span> <span className="text-muted-foreground">{l.travellers}{l.total_seats ? `/${l.total_seats}` : ""}</span></p>
              ))}
              {tall && info.locations.flatMap((l) => l.trips.flatMap((t) => t.items || [])).slice(0, 6).map((b) => (
                <p key={b.id} className="text-[10px] text-muted-foreground truncate flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLOR[b.status] || "bg-muted-foreground"}`} />{b.name} · {b.travellers}p</p>
              ))}
              {!tall && info.locations.length > 3 && <p className="text-[10px] text-muted-foreground">+{info.locations.length - 3} more</p>}
            </div>
          </>
        )}
      </button>
    );
  };

  const dayInfo = byDate[iso(cursor)];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-4xl font-semibold">Booking Calendar</h1><p className="text-muted-foreground">{isAdmin ? "All bookings and departures" : "Your bookings"} by date.</p></div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border bg-card p-0.5" data-testid="calendar-view-toggle">
            {VIEWS.map((v) => <button key={v} type="button" onClick={() => setView(v)} className={`px-3 py-1 rounded-full text-sm capitalize transition-colors ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`calendar-view-${v}`}>{v}</button>)}
          </div>
          <Button variant="outline" size="icon" onClick={() => step(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-heading text-lg sm:text-xl min-w-[160px] text-center" data-testid="calendar-month">{title}</span>
          <Button variant="outline" size="icon" onClick={() => step(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); }} data-testid="calendar-today">Today</Button>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="calendar-filters">
        <Select value={loc} onValueChange={setLoc}><SelectTrigger data-testid="calendar-location-filter"><MapPin className="h-4 w-4 mr-1 text-muted-foreground" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All locations</SelectItem>{locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
        <Select value={fTrip} onValueChange={setFTrip}><SelectTrigger data-testid="calendar-trip-filter"><MapIcon className="h-4 w-4 mr-1 text-muted-foreground" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All trips</SelectItem>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger data-testid="calendar-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{["Confirmed", "Payment Verification Pending", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        {isAdmin && <Select value={fEmp} onValueChange={setFEmp}><SelectTrigger data-testid="calendar-employee-filter"><UserRound className="h-4 w-4 mr-1 text-muted-foreground" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All employees</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>)}</SelectContent></Select>}
      </div>

      <div className="flex flex-wrap gap-3 text-sm" data-testid="calendar-totals">
        <span className="rounded-full border bg-card px-3 py-1">{totals.b} bookings</span>
        <span className="rounded-full border bg-card px-3 py-1">{totals.t} travellers</span>
        <span className="rounded-full border bg-card px-3 py-1">{Object.keys(byDate).length} departure days</span>
      </div>

      {view !== "day" && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-7 border-b text-xs font-medium text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="p-2 text-center">{d}</div>)}
          </div>
          <div className={`grid grid-cols-7 ${view === "month" ? "auto-rows-[minmax(96px,auto)]" : ""}`}>
            {(view === "month" ? monthCells() : weekCells()).map((ds, i) => ds ? <DayCell key={ds} ds={ds} tall={view === "week"} /> : <div key={`e${i}`} className="border-b border-r bg-muted/20" />)}
          </div>
        </div>
      )}

      {view === "day" && (
        <div className="rounded-xl border bg-card p-4 space-y-4" data-testid="calendar-day-view">
          {!dayInfo && <p className="text-sm text-muted-foreground py-10 text-center">No bookings on this day.</p>}
          {dayInfo?.locations.map((l) => <LocationBlock key={l.destination} l={l} />)}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{sel?.date} · {sel?.travellers} travellers · {sel?.bookings} bookings</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="calendar-day-detail">
            {sel?.locations.map((l) => <LocationBlock key={l.destination} l={l} />)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationBlock({ l }) {
  return (
    <div className="rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <p className="font-medium flex items-center gap-1"><MapPin className="h-4 w-4 text-primary" /> {l.destination}</p>
        <Badge variant="outline" className="rounded-full">{l.travellers}{l.total_seats ? ` / ${l.total_seats} seats` : ""} · {l.bookings} bkg</Badge>
      </div>
      <div className="mt-3 space-y-2">
        {l.trips.map((t) => (
          <div key={t.trip_id} className="text-sm">
            <p className="font-medium">{t.trip_name} <span className="text-muted-foreground font-normal">— {t.travellers} pax{t.total_seats ? ` of ${t.total_seats}` : ""}</span></p>
            {(t.items || []).map((b) => (
              <div key={b.id} className="ml-3 text-xs text-muted-foreground flex flex-wrap items-center gap-2 py-0.5" data-testid="calendar-booking-item">
                <span className={`h-2 w-2 rounded-full ${STATUS_COLOR[b.status] || "bg-muted-foreground"}`} /> {b.booking_id || "Pending ID"} · {b.name} · {b.travellers} pax
                {b.employee_code && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{b.employee_code}</span>}
                {b.balance_due > 0 && <span className="text-amber-600 dark:text-amber-400">due {inr(b.balance_due)}</span>}
                {b.whatsapp && <a href={buildWaLink(b.whatsapp, `Hi ${b.name}, this is Safarshala regarding your booking ${b.booking_id || ""} for ${t.trip_name}.`)} target="_blank" rel="noreferrer" className="text-emerald-600 dark:text-emerald-400"><MessageCircle className="h-3.5 w-3.5" /></a>}
              </div>
            ))}
            {!t.items?.length && <p className="ml-3 text-xs text-muted-foreground">No bookings yet</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
